import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';
import { formatWord } from 'utils/helperMethods';

type TimePeriodOption = {
    timePeriod?: string;
    startTimestamp?: number;
    endTimestamp?: number;
};

const formatTimePeriodLabel = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return '';
    return trimmed.length < 5 ? trimmed.toUpperCase() : trimmed;
};

const resolveTimePeriodLabel = ({
    timeSelectionLabel,
    chartRequest,
    timePeriodList,
}: {
    timeSelectionLabel?: string;
    chartRequest?: PricePackArchitectureRequest | null;
    timePeriodList?: TimePeriodOption[];
}) => {
    if (timeSelectionLabel?.trim()) {
        return formatTimePeriodLabel(timeSelectionLabel);
    }

    const timePeriod = chartRequest?.time_period;
    if (!timePeriod?.start_timestamp || !timePeriod?.end_timestamp || !timePeriodList?.length) {
        return undefined;
    }

    const match = timePeriodList.find(
        (period) =>
            period.startTimestamp === timePeriod.start_timestamp &&
            period.endTimestamp === timePeriod.end_timestamp,
    );

    return match?.timePeriod ? formatTimePeriodLabel(match.timePeriod) : undefined;
};

export const buildPackArchitectureGlobalFiltersSummary = ({
    timeSelectionLabel,
    chartRequest,
    timePeriodList,
    allCheckedAttribute,
}: {
    timeSelectionLabel?: string;
    chartRequest?: PricePackArchitectureRequest | null;
    timePeriodList?: TimePeriodOption[];
    allCheckedAttribute?: Record<string, Record<string, unknown>>;
}) => {
    const parts: string[] = [];

    const timePeriodLabel = resolveTimePeriodLabel({
        timeSelectionLabel,
        chartRequest,
        timePeriodList,
    });
    if (timePeriodLabel) {
        parts.push(`Time Period: ${timePeriodLabel}`);
    }

    Object.entries(allCheckedAttribute || {})
        .map(([attributeType, selectedValues]) => ({
            attributeType,
            label: formatWord(attributeType),
            items: Object.keys((selectedValues as Record<string, unknown>) || {}),
        }))
        .filter((group) => group.items.length > 0)
        .sort((left, right) => left.label.localeCompare(right.label))
        .forEach((group) => {
            parts.push(`${group.label}: ${group.items.join(', ')}`);
        });

    return parts.join(' | ');
};
